import React, { useMemo } from "react";
import { TaxJurisdiction } from "@moonscape/shared";
import { cn } from "@/utils/shadcn";
import { DHead, LAB, NUM } from "@/components/ms";

interface DateNode {
  date: Date;
  label: string;
  detail: string;
}

interface KeyDatesTimelineProps {
  taxYear: number;
  jurisdiction?: string;
}

function getKeyDates(taxYear: number, jurisdiction?: string): DateNode[] {
  const filingYear = taxYear + 1;

  if (jurisdiction === TaxJurisdiction.UK) {
    return [
      { date: new Date(taxYear, 3, 6), label: "Apr 6", detail: "Tax year starts" },
      { date: new Date(filingYear, 3, 5), label: "Apr 5", detail: "Tax year ends" },
      { date: new Date(filingYear, 9, 31), label: "Oct 31", detail: "Paper filing deadline" },
      { date: new Date(filingYear + 1, 0, 31), label: "Jan 31", detail: "Online filing + payment" },
    ];
  }

  if (jurisdiction === TaxJurisdiction.US) {
    return [
      { date: new Date(taxYear, 0, 1), label: "Jan 1", detail: "Tax year starts" },
      { date: new Date(filingYear, 0, 31), label: "Jan 31", detail: "1099 forms issued by exchanges" },
      { date: new Date(filingYear, 3, 15), label: "Apr 15", detail: "Filing deadline" },
      { date: new Date(filingYear, 9, 15), label: "Oct 15", detail: "Extension deadline" },
    ];
  }

  // Default
  return [
    { date: new Date(taxYear, 0, 1), label: "Jan 1", detail: "Tax year starts" },
    { date: new Date(filingYear, 3, 15), label: "Apr 15", detail: "Estimated deadline" },
  ];
}

type NodeStatus = "past" | "current" | "future";

interface DisplayNode {
  label: string;
  detail: string;
  status: NodeStatus;
  isToday?: boolean;
}

export const KeyDatesTimeline: React.FC<KeyDatesTimelineProps> = ({
  taxYear,
  jurisdiction,
}) => {
  const nodes = useMemo((): DisplayNode[] => {
    const keyDates = getKeyDates(taxYear, jurisdiction);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const result: DisplayNode[] = [];
    let todayInserted = false;

    for (let i = 0; i < keyDates.length; i++) {
      const node = keyDates[i];
      const nodeDate = new Date(node.date);
      nodeDate.setHours(0, 0, 0, 0);

      // Insert "Today" before the first future node
      if (!todayInserted && today < nodeDate) {
        const daysUntilNext = Math.ceil(
          (nodeDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        result.push({
          label: "Today",
          detail: `${today.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — ${daysUntilNext} days to next`,
          status: "current",
          isToday: true,
        });
        todayInserted = true;
      }

      const isPast = today > nodeDate;
      const isSameDay = today.getTime() === nodeDate.getTime();

      result.push({
        label: node.label,
        detail: node.detail,
        status: isSameDay ? "current" : isPast ? "past" : "future",
      });
    }

    // If today is after all dates, append it at the end
    if (!todayInserted) {
      result.push({
        label: "Today",
        detail: today.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        status: "current",
        isToday: true,
      });
    }

    return result;
  }, [taxYear, jurisdiction]);

  return (
    <section className="flex flex-col">
      <DHead label={`${taxYear} key dates`} right={jurisdiction} />

      {/* Timeline */}
      <div className="relative mt-5 flex items-start overflow-x-auto pb-2">
        {/* Connecting line */}
        <div className="absolute left-8 right-8 top-[4px] h-px bg-ms-line" />

        {nodes.map((node, i) => (
          <div
            key={i}
            className="relative flex min-w-[100px] flex-1 flex-col items-center gap-2 text-center"
          >
            {/* Dot */}
            <span
              className={cn(
                "relative z-10 h-[9px] w-[9px] flex-none rounded-full",
                node.status === "past" && "bg-ms-green",
                node.status === "current" && "bg-ms-tan ring-4 ring-ms-tan-soft",
                node.status === "future" && "bg-ms-dim"
              )}
            />

            {/* Label */}
            <span
              className={cn(
                NUM,
                "text-[11px] font-semibold",
                node.status === "past" && "text-ms-green",
                node.status === "current" && "text-ms-tan",
                node.status === "future" && "text-ms-ink"
              )}
            >
              {node.label}
            </span>

            {/* Detail */}
            <span
              className={cn(
                LAB,
                "max-w-[120px] text-[9px] leading-[1.5] text-ms-dim"
              )}
            >
              {node.detail}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};
